import { useSelector } from '@tanstack/react-form';
import { isFormField } from '@tronche2tek/shared-types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CardSides, CardSource } from '../../../api/cards';
import {
  fieldsForSides,
  sourceForType,
  useCardPreview,
  useCardTemplates,
} from '../../../api/cards';
import { useIseeEnabled } from '../../../api/isee';
import { useLuccaEnabled } from '../../../api/lucca';
import type { User } from '../../../types';
import { useLatest } from '../../../utils/use-latest';
import { Spinner } from '../Spinner';
import { buildPreflightInput } from './single-card';
import { SingleCardForm } from './SingleCardForm';
import { PreviewModal, ResultView } from './SingleCardResult';
import {
  useSingleCardForm,
  useSingleCardRun,
  useTemplatePick,
} from './use-single-card';

// ─────────────────────────────────────────────────────────────────────────────
// Container
// ─────────────────────────────────────────────────────────────────────────────

export function SingleCardGenerator({ user }: { user: User }) {
  const { t } = useTranslation();
  const iseeEnabled = useIseeEnabled();
  const luccaEnabled = useLuccaEnabled();
  const savedTemplates = useCardTemplates();

  // What the operator picks. The template's own metadata — its SVG, its Liquid
  // vars, its stored defaults — is derived from that choice, not typed, so it
  // lives in useTemplatePick rather than in the form.
  const form = useSingleCardForm(() => void generateRef.current());
  // One selector per field, so a change re-renders this component only for the
  // fields it actually reads.
  const selectedTemplateId = useSelector(
    form.store,
    (state) => state.values.templateId,
  );
  const customTemplateFile = useSelector(
    form.store,
    (state) => state.values.customTemplateFile,
  );
  const fieldValues = useSelector(
    form.store,
    (state) => state.values.fieldValues,
  );
  const sides = useSelector(form.store, (state) => state.values.sides);

  const template = useTemplatePick(form);
  const { resolvedSvg } = template;
  const run = useSingleCardRun(template.clear);
  const { phase, result, jobId, error } = run;
  const [previewOpen, setPreviewOpen] = useState<string | null>(null);

  // The card source is driven by the selected template's type: student → ISEE
  // (the current enrollment), employee → Lucca, other/custom SVG → plain CSV row.
  const activeTpl = savedTemplates.find((tpl) => tpl.id === selectedTemplateId);
  const source = sourceForType(activeTpl?.type);

  // A single-side choice only applies when the template has a verso.
  const effectiveSides: CardSides = activeTpl?.backSide ? sides : 'both';

  // Fields the generated side(s) actually use.
  const extraFields = useMemo(
    () =>
      fieldsForSides(effectiveSides, template.frontExtras, template.backExtras),
    [effectiveSides, template.frontExtras, template.backExtras],
  );

  // Subset of extraFields flagged `form:` in the defaults — surfaced as inputs
  // for student/employee templates and sent as `values` (one per whole card).
  const formFields = useMemo(
    () => extraFields.filter((f) => isFormField(template.defaults[f])),
    [extraFields, template.defaults],
  );

  const preview = useCardPreview(
    jobId,
    phase === 'done' && result?.status === 'success',
    user.userPrincipalName,
  );

  // Reset the generation output when the target user changes (this component
  // stays mounted while the /users/:oid route param changes) so a card
  // generated for a previous user doesn't linger. Done during render — the
  // React-recommended way to reset state on a prop change — so the stale card
  // never paints. Template/field selections are intentionally kept.
  const [trackedUpn, setTrackedUpn] = useState(user.userPrincipalName);
  if (user.userPrincipalName !== trackedUpn) {
    setTrackedUpn(user.userPrincipalName);
    setPreviewOpen(null);
    run.reset();
  }

  const generate = () => {
    if (!resolvedSvg) {
      return Promise.resolve();
    }
    return run.start(
      buildPreflightInput({
        source,
        upn: user.userPrincipalName,
        templateId: selectedTemplateId,
        resolvedSvg,
        sides: effectiveSides,
        extraFields,
        formFields,
        fieldValues,
      }),
    );
  };
  // `onSubmit` is captured by useForm on the first render, so it calls through
  // a ref rather than closing over this render's `generate`.
  const generateRef = useLatest(generate);

  const canGenerate = canGenerateCard({
    resolvedSvg,
    source,
    iseeEnabled,
    luccaEnabled,
    formFields,
    fieldValues,
  });

  return (
    // No rule and no heading of its own: the card head above already draws one
    // and already says "Génération de carte", so this drew a second separator
    // under the first and repeated the title a line below it — the same string
    // the button at the bottom carries.
    <div>
      {(phase === 'idle' || phase === 'error') && (
        <SingleCardForm
          form={form}
          template={template}
          savedTemplates={savedTemplates}
          selectedTemplateId={selectedTemplateId}
          activeTpl={activeTpl}
          customTemplateFile={customTemplateFile}
          source={source}
          sides={sides}
          fieldValues={fieldValues}
          extraFields={extraFields}
          formFields={formFields}
          iseeEnabled={iseeEnabled}
          luccaEnabled={luccaEnabled}
          canGenerate={canGenerate}
          error={error}
          onSubmit={() => void form.handleSubmit()}
        />
      )}

      {phase === 'uploading' && (
        <BusyLine label={t('singleCardGenerator.preparing')} />
      )}

      {phase === 'processing' && (
        <BusyLine
          label={t('singleCardGenerator.generating', {
            name: user.displayName,
          })}
        />
      )}

      {phase === 'done' && (
        <ResultView
          result={result}
          preview={preview}
          upn={user.userPrincipalName}
          sides={effectiveSides}
          onPreview={setPreviewOpen}
          onReset={run.reset}
        />
      )}

      {previewOpen && (
        <PreviewModal url={previewOpen} onClose={() => setPreviewOpen(null)} />
      )}
    </div>
  );
}

/** Whether everything the active source needs is there to generate. */
function canGenerateCard({
  resolvedSvg,
  source,
  iseeEnabled,
  luccaEnabled,
  formFields,
  fieldValues,
}: {
  resolvedSvg: string | null;
  source: CardSource;
  iseeEnabled: boolean;
  luccaEnabled: boolean;
  formFields: string[];
  fieldValues: Record<string, string>;
}): boolean {
  // Every `form:` field must be filled — a blank one would render an empty value.
  const hasEmptyFormField = formFields.some(
    (f) => (fieldValues[f] ?? '').trim() === '',
  );
  return (
    !!resolvedSvg &&
    (source !== 'isee' || iseeEnabled) &&
    (source !== 'lucca' || luccaEnabled) &&
    !hasEmptyFormField
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Request builder
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Presentational subcomponents
// ─────────────────────────────────────────────────────────────────────────────

function BusyLine({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted">
      <Spinner /> {label}
    </div>
  );
}
